import {Component, Vue} from 'vue-property-decorator';
import moment from 'moment';
import ApiAuthorizationError from '@/errors/ApiAuthorizationError';
import ApiValidationError from '@/errors/ApiValidationError';
import ApiExcessiveSignInError from '@/errors/ApiExcessiveSignInError';

@Component({
    filters: {
        formatCurrency(value: number): string {
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2,
            });

            return formatter.format(value);
        },
        formatDateTime(value?: Date): any {
            if (!value) {
              return value;
            }

            return moment(value).format('YYYY-MM-DD HH:mm');
        },
        formatDate(value?: Date): any {
          if (!value) {
            return value;
          }

          return moment(value).format('YYYY-MM-DD');
        },
        formatInteger(value: number): any {
          if (typeof value !== 'number') {
            return value;
          }
          const formatter = new Intl.NumberFormat('en-US', {
              minimumFractionDigits: 0,
          });

          return formatter.format(value);
        },
        padId(value: number): string {
          const str = value.toString(10);
          return str.padStart(10, '0');
        },
        booleanToYesNo(value: boolean): string {
          if ( value )
          {
            return 'YES';
          }
          else
          {
            return 'NO';
          }
        },
    },
})
export default class ViewBase extends Vue {
    public initializing: boolean = true;
    public initializationError: boolean = false;
    public processing: boolean = false;
    public processingError: boolean = false;
    public processingErrors: string[] = [];

    protected resetInitializationValues() {
        this.initializing = true;
        this.initializationError = false;
    }

    protected resetProcessingValues() {
        this.processing = true;
        this.processingError = false;
        this.processingErrors = [];
    }

    protected amountRules(value: string, allowZero: boolean): any {
      if ( value.trim().length > 0)
      {
          // only allow numbers and decimal
          const rx = /^\d*\.?\d+$/;

          if ( !value.match(rx) )
          {
              return 'Amount must be numeric and not negative.';
          }
          else
          {
              const amount = parseFloat(value);

              if ( !allowZero ) {
                if ( amount === 0 ) {
                  return 'The amount may not be zero.';
                }
              }

              return true;
          }
      }
      else
      {
          return 'An amount is required.';
      }
    }

    // natural number is a whole number (0, 1, 2, 3, ...) or a counting number (1, 2, 3, ...) without any fractions
    protected isNaturalNumber(value: string = ''): boolean {
      if ( value.trim().length === 0) {
        return false;
      }

      const rx = /^\d+$/; // only allow numbers

      return rx.test(value);
    }

    // a positive number that can have fractions
    protected isPositiveRationalNumber(value: string = ''): boolean {
      if ( value.trim().length === 0) {
        return false;
      }

      const rx = /^\d*\.?\d+$/; // allow numbers and a decimal

      return rx.test(value);
    }

    protected async handleInitializationErrorAsync(error: any) {
      if ( error instanceof ApiAuthorizationError ) {
        await this.signOutAsync();
      }

      this.initializationError = true;
    }

    protected async handleProcessingErrorAsync(error: any) {
      if ( error instanceof ApiValidationError ) {
        this.processingErrors = error.getErrorDescriptions();
      }
      else if ( error instanceof ApiAuthorizationError ) {
        await this.signOutAsync();
      }
      else if ( error instanceof ApiExcessiveSignInError ) {
        await this.$router.push({name: 'signinpause'});
      }
      else {
        this.processingErrors.push('An error occurred while processing your request.');
      }

      this.processingError = true;
    }

    protected async signOutAsync() {
      this.$store.dispatch('signOut');
      this.$store.commit('mutateSnackbarText', 'You have been signed out.');
      this.$store.commit('mutateShowSnackbar', true);
      await this.$router.push({name: 'signin'});
    }
}
